import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardBody, CardTitle } from 'reactstrap'
import Request from '../../../helpers/request'
import * as url from '../../../helpers/url_helper'

// Migration Tracker (Ram 2026-08-08): place-wise v4.x migration. Columns:
// owner contact | place | total modules | Updated to latest (the 3.x wave
// count — NOT migration) | Migrated (v4.x, real evidence only: the durable
// migratedToV2 marker / a 4.x fw sighting) | Pending | Failed | Master ready
// for OTA (READ-ONLY pill: Waiting -> Ready once every non-master node has
// v4.x, Done once the master is across too) | Master Migrated.
// This page is an OBSERVER: master issuance is V2-owned, so nothing here
// triggers an OTA. See the backend note on migrateMaster (still 410).
// Client-side search (user name/email/mobile, place) + pagination.
// The backend stamps semantics:'v4x-migration'; older backends get a loud
// banner instead of silently mislabeled counts.

const TITLE_STYLE = { color: '#5D6679', fontWeight: 500, fontSize: 16 }
const CAPTION = { fontSize: 11, color: '#B8BFC9', marginBottom: 0 } as const
const PAGE_SIZE = 15

const pill = (bg: string, fg: string, label: string) => (
    <span style={{ background: bg, color: fg, padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 500 }}>
        {label}
    </span>
)

const MigrateStatus = () => {
    const [data, setData] = useState<any>(null)
    const [err, setErr] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [query, setQuery] = useState('')
    const [page, setPage] = useState(1)
    // 'firing' / 'notice' removed with the Migrate-Master button — this page
    // is read-only and has no in-flight action to report.

    const fetchData = () => {
        setBusy(true)
        setErr(null)
        Request.post(url.OTA_MIGRATE_STATUS, {})
            .then((res: any) => setData(res?.data?.data || null))
            .catch((e: any) => {
                setData(null)
                const s = e?.response?.status
                setErr(s === 410
                    // Backend still carries cefc99f's retirement of this read
                    // endpoint. Name it exactly — the old copy said "not
                    // exposed yet", which sent the reader looking for a
                    // missing feature instead of a retired one.
                    ? 'Backend has this endpoint retired (410). It needs the migrate-status restore deployed.'
                    : s === 404
                        ? 'This backend version does not expose the migrate-status endpoint yet.'
                        : 'Failed to load migration status — use Refresh to retry.')
            })
            .finally(() => setBusy(false))
    }
    useEffect(() => { fetchData() }, [])

    // Master issuance is V2-owned (backend retired /admin/v1/ota/migrate-master
    // in cefc99f and it stays retired). This page is an OBSERVER only — it
    // reports master readiness, it does not trigger anything.

    const places = data?.places || []
    const v4x = data?.semantics === 'v4x-migration'
    const totals = places.reduce((a: any, p: any) => ({
        modules: a.modules + (p.totalModules || 0),
        migrated: a.migrated + (p.migrated || 0),
        pending: a.pending + (p.pending || 0),
        failed: a.failed + (p.failed || 0),
    }), { modules: 0, migrated: 0, pending: 0, failed: 0 })

    // Search over user name / email / mobile / place name.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return places
        return places.filter((p: any) =>
            [p.user?.name, p.user?.email, p.user?.mobile, p.placeName]
                .some((v) => v && String(v).toLowerCase().includes(q)))
    }, [places, query])

    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
    const safePage = Math.min(page, pageCount)
    const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

    // Read-only master state. Was a "Migrate Master" action button; issuance is
    // V2-owned, so this now reports state and triggers nothing.
    const PILL: any = { padding: '3px 12px', fontSize: 12, borderRadius: 6, fontWeight: 600, display: 'inline-block' }
    const masterCell = (p: any) => {
        if (p.masterMigrated) {
            return <span style={{ ...PILL, background: '#EFF8FF', color: '#175CD3', border: '1px solid #B2DDFF' }}>Done</span>
        }
        if (p.masterReady === 'yes') {
            return <span style={{ ...PILL, background: '#ECFDF3', color: '#027A48', border: '1px solid #ABEFC6' }}
                title="All nodes migrated — master is eligible; issuance is done from V2">Ready</span>
        }
        return <span style={{ ...PILL, background: '#F3F4F6', color: '#98A2B3', border: '1px solid #E5E7EB' }}
            title="Waiting until every node except the master has v4.x">Waiting</span>
    }

    return (
        <div className="page-content">
            <Card style={{ borderRadius: 10 }}>
                <CardBody>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <CardTitle style={{ ...TITLE_STYLE, marginBottom: 0 }}>Migration Tracker</CardTitle>
                        {data && (
                            <span style={{ fontSize: 12, color: '#858D9D' }}>
                                mode <b>{data.mode}</b> · fleet {totals.migrated} / {totals.modules} migrated · {totals.pending} pending{totals.failed ? ` · ${totals.failed} failed` : ''}
                            </span>
                        )}
                        <input
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setPage(1) }}
                            placeholder="Search user / email / mobile / place…"
                            style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12.5, border: '1px solid #D1D5DB', borderRadius: 6, minWidth: 240 }}
                        />
                        <button onClick={fetchData} disabled={busy}
                            style={{ padding: '3px 10px', fontSize: 12, background: '#fff', border: '1px solid #D1D5DB', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer' }}>
                            {busy ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>

                    {data && !v4x && (
                        <p style={{ marginTop: 10, fontSize: 12.5, color: '#B54708', background: '#FFFAEB', padding: '6px 10px', borderRadius: 6 }}>
                            This backend still reports the old counts ("reached latest 3.x"), NOT v4.x migration —
                            the numbers below are firmware-update progress, not migration. A backend deploy fixes this.
                        </p>
                    )}

                    {err ? (
                        <p style={{ marginTop: 10, fontSize: 13, color: '#B54708' }}>{err}</p>
                    ) : !data ? (
                        <p style={{ marginTop: 10, fontSize: 13, color: '#858D9D' }}>Loading…</p>
                    ) : filtered.length === 0 ? (
                        <p style={{ marginTop: 10, fontSize: 13, color: '#858D9D' }}>
                            {query ? `No places match "${query}".` : 'No places with migratable modules found.'}
                        </p>
                    ) : (
                        <>
                        <table style={{ width: '100%', marginTop: 12, fontSize: 12.5, borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ color: '#858D9D', textAlign: 'left' }}>
                                    <th style={{ padding: '5px 8px' }}>User</th>
                                    <th style={{ padding: '5px 8px' }}>Place</th>
                                    <th style={{ padding: '5px 8px' }}>Total modules</th>
                                    <th style={{ padding: '5px 8px' }}>Updated to latest</th>
                                    <th style={{ padding: '5px 8px', color: '#027A48' }}>Migrated</th>
                                    <th style={{ padding: '5px 8px', color: '#B42318' }}>Pending</th>
                                    <th style={{ padding: '5px 8px', color: '#B42318' }}>Failed</th>
                                    <th style={{ padding: '5px 8px' }}>Master ready for OTA</th>
                                    <th style={{ padding: '5px 8px' }}>Master migrated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((p: any, i: number) => (
                                    <tr key={p.placeId || `row-${i}`} style={{ borderTop: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '5px 8px' }}>
                                            {p.user?.name || <span style={{ color: '#B8BFC9' }}>—</span>}
                                            <div style={{ fontSize: 11, color: '#858D9D' }}>
                                                {[p.user?.email, p.user?.mobile].filter(Boolean).join(' · ') || 'no contact on record'}
                                            </div>
                                        </td>
                                        <td style={{ padding: '5px 8px' }}>{p.placeName || <span style={{ color: '#B8BFC9' }}>(unnamed)</span>}</td>
                                        <td style={{ padding: '5px 8px' }}>{p.totalModules}</td>
                                        <td style={{ padding: '5px 8px' }}>{p.updatedToLatest ?? (v4x ? 0 : p.migrated)}</td>
                                        <td style={{ padding: '5px 8px' }}>{v4x ? p.migrated : 0}</td>
                                        <td style={{ padding: '5px 8px', fontWeight: p.pending ? 600 : 400 }}>{p.pending}</td>
                                        <td style={{ padding: '5px 8px', color: p.failed ? '#B42318' : undefined, fontWeight: p.failed ? 600 : 400 }}>{p.failed ?? 0}</td>
                                        <td style={{ padding: '5px 8px' }}>{masterCell(p)}</td>
                                        <td style={{ padding: '5px 8px' }}>
                                            {p.masterMigrated ? pill('#EFF8FF', '#175CD3', 'Yes') : pill('#F3F4F6', '#475467', 'No')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 12.5 }}>
                            <span style={{ color: '#858D9D' }}>
                                {filtered.length} place(s){query ? ` matching "${query}"` : ''} · page {safePage} / {pageCount}
                            </span>
                            <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}
                                style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: 12, background: '#fff', border: '1px solid #D1D5DB', borderRadius: 4, cursor: safePage <= 1 ? 'not-allowed' : 'pointer' }}>
                                Prev
                            </button>
                            <button disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}
                                style={{ padding: '3px 10px', fontSize: 12, background: '#fff', border: '1px solid #D1D5DB', borderRadius: 4, cursor: safePage >= pageCount ? 'not-allowed' : 'pointer' }}>
                                Next
                            </button>
                        </div>
                        </>
                    )}
                    <p style={{ ...CAPTION, marginTop: 8 }}>
                        Migrated = real v4.x evidence only (v2-confirmed marker or a 4.x firmware sighting), counted over
                        mesh nodes — the master has its own columns. "Updated to latest" = modules current on the 3.x
                        stable build (firmware-update progress, the prerequisite for migration). "Master ready for OTA"
                        reads Ready once every node except the master has v4.x; "Done" = the master is across too.
                        This page reports only — master issuance is performed from V2, not from here.
                        Placeholder sockets and retired types are excluded from all counts.
                    </p>
                </CardBody>
            </Card>
        </div>
    )
}

export default MigrateStatus
